import { useState, useRef, useEffect, useCallback } from "react";
import { Sparkles, Loader2, Check, RotateCcw, X, ChevronDown, Copy, Wand2 } from "lucide-react";
import { generateAiContent } from "../../utils/aiApi";
import { useContentEditingSafe } from "./ContentEditingContext";

/* ────────────────────────────────────────────
   AI Writer Modal
   
   A full-screen modal for generating AI content
   for any text element on the page.
   
   Can work in two modes:
   1. "free" - user picks what to generate (heading, paragraph, etc.)
   2. "targeted" - pre-filled with a specific content key
   ──────────────────────────────────────────── */

/** Content generation presets */
const GENERATION_TYPES: { id: string; label: string; elementType: string; description: string; icon: string }[] = [
  { id: "h1", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05E8\u05D0\u05E9\u05D9\u05EA", elementType: "h1", description: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05D7\u05D6\u05E7\u05D4 \u05D5\u05E7\u05E6\u05E8\u05D4 \u05DC\u05D0\u05D6\u05D5\u05E8 \u05D4\u05E8\u05D0\u05E9\u05D9", icon: "\uD83C\uDFAF" },
  { id: "h2", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05E1\u05E7\u05E6\u05D9\u05D4", elementType: "h2", description: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05DE\u05E9\u05E0\u05D9\u05EA \u05DC\u05E1\u05E7\u05E6\u05D9\u05D4 \u05D1\u05D0\u05EA\u05E8", icon: "\uD83D\uDCCC" },
  { id: "paragraph", label: "\u05E4\u05E1\u05E7\u05D4 \u05E9\u05D9\u05D5\u05D5\u05E7\u05D9\u05EA", elementType: "p", description: "\u05E4\u05E1\u05E7\u05EA \u05EA\u05D5\u05DB\u05DF \u05E9\u05D9\u05D5\u05D5\u05E7\u05D9\u05EA \u05DE\u05E9\u05DB\u05E0\u05E2\u05EA", icon: "\u270D\uFE0F" },
  { id: "cta", label: "\u05E7\u05E8\u05D9\u05D0\u05D4 \u05DC\u05E4\u05E2\u05D5\u05DC\u05D4", elementType: "button", description: "\u05D8\u05E7\u05E1\u05D8 \u05DC\u05DB\u05E4\u05EA\u05D5\u05E8 CTA \u05D7\u05D6\u05E7", icon: "\uD83D\uDE80" },
  { id: "list", label: "\u05E4\u05E8\u05D9\u05D8 \u05D1\u05E8\u05E9\u05D9\u05DE\u05D4", elementType: "li", description: "\u05E4\u05E8\u05D9\u05D8 \u05D9\u05EA\u05E8\u05D5\u05DF \u05D0\u05D5 \u05EA\u05DB\u05D5\u05E0\u05D4 \u05DC\u05E8\u05E9\u05D9\u05DE\u05D4", icon: "\u2705" },
  { id: "about", label: "\u05E4\u05E1\u05E7\u05EA \u05D0\u05D5\u05D3\u05D5\u05EA", elementType: "p", description: "\u05EA\u05D9\u05D0\u05D5\u05E8 \u05E2\u05E1\u05E7 / \u05D0\u05D5\u05D3\u05D5\u05EA\u05D9\u05E0\u05D5", icon: "\uD83C\uDFE2" },
];

/** Tone presets */
const TONE_PRESETS: { label: string; value: string }[] = [
  { label: "\u05E9\u05D9\u05D5\u05D5\u05E7\u05D9", value: "\u05D1\u05E1\u05D2\u05E0\u05D5\u05DF \u05E9\u05D9\u05D5\u05D5\u05E7\u05D9 \u05D5\u05DE\u05E9\u05DB\u05E0\u05E2" },
  { label: "\u05DE\u05E7\u05E6\u05D5\u05E2\u05D9", value: "\u05D1\u05E1\u05D2\u05E0\u05D5\u05DF \u05DE\u05E7\u05E6\u05D5\u05E2\u05D9 \u05D5\u05D0\u05DE\u05D9\u05DF" },
  { label: "\u05D7\u05DE\u05D5\u05D3 \u05D5\u05E0\u05D2\u05D9\u05E9", value: "\u05D1\u05E1\u05D2\u05E0\u05D5\u05DF \u05D7\u05DE\u05D5\u05D3 \u05D5\u05D0\u05D9\u05E9\u05D9" },
  { label: "\u05E8\u05E9\u05DE\u05D9", value: "\u05D1\u05E1\u05D2\u05E0\u05D5\u05DF \u05E8\u05E9\u05DE\u05D9 \u05D5\u05DE\u05DB\u05D5\u05D1\u05D3" },
  { label: "\u05EA\u05DE\u05E6\u05D9\u05EA\u05D9", value: "\u05D1\u05E1\u05D2\u05E0\u05D5\u05DF \u05EA\u05DE\u05E6\u05D9\u05EA\u05D9 \u05D5\u05D9\u05E9\u05D9\u05E8" },
];

interface AiWriterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AiWriterModal({ isOpen, onClose }: AiWriterModalProps) {
  const ctx = useContentEditingSafe();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [tone, setTone] = useState("");
  const [customPrompt, setCustomPrompt] = useState("");
  const [businessContext, setBusinessContext] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedType(null);
      setGeneratedContent(null);
      setError(null);
      setHistory([]);
      setCopiedIndex(null);
    }
  }, [isOpen]);

  // Focus prompt when type is selected
  useEffect(() => {
    if (selectedType && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [selectedType]);

  const handleGenerate = useCallback(async () => {
    if (!selectedType) return;
    const typeConfig = GENERATION_TYPES.find((t) => t.id === selectedType);
    if (!typeConfig) return;

    setIsGenerating(true);
    setError(null);

    try {
      // Build combined prompt from tone + custom
      let fullPrompt = "";
      if (tone) fullPrompt += tone + ". ";
      if (customPrompt.trim()) fullPrompt += customPrompt.trim();

      const result = await generateAiContent({
        elementType: typeConfig.elementType,
        contentKey: selectedType === "about" ? "about:description" : `${selectedType}:content`,
        currentContent: "",
        businessName: businessContext.trim() || undefined,
        businessType: "",
        pageContext: "",
        userPrompt: fullPrompt || undefined,
      });

      setGeneratedContent(result);
      setHistory((prev) => [result, ...prev].slice(0, 5));
    } catch (err) {
      console.error("[AiModal] Generation error:", err);
      setError(err instanceof Error ? err.message : "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D9\u05E6\u05D9\u05E8\u05EA \u05EA\u05D5\u05DB\u05DF");
    } finally {
      setIsGenerating(false);
    }
  }, [selectedType, tone, customPrompt, businessContext]);

  const handleCopy = useCallback((text: string, index: number) => {
    // Use document.execCommand("copy") per project rules
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);

    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        dir="rtl"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col"
        style={{ fontFamily: "var(--font-family)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100" style={{ background: "linear-gradient(135deg, #052b7a 0%, #1a4494 100%)" }}>
          <div className="flex items-center gap-2.5 text-white">
            <div className="w-8 h-8 bg-white/15 rounded-lg flex items-center justify-center">
              <Sparkles className="w-4.5 h-4.5" />
            </div>
            <div>
              <h2 className="text-base leading-tight" style={{ fontWeight: 700 }}>
                {"\u05E2\u05D5\u05D6\u05E8 \u05EA\u05D5\u05DB\u05DF AI"}
              </h2>
              <p className="text-[11px] text-white/60 leading-tight">
                {"\u05E6\u05D5\u05E8 \u05EA\u05D5\u05DB\u05DF \u05E9\u05D9\u05D5\u05D5\u05E7\u05D9 \u05D1\u05E2\u05D1\u05E8\u05D9\u05EA \u05D1\u05DC\u05D7\u05D9\u05E6\u05EA \u05DB\u05E4\u05EA\u05D5\u05E8"}
              </p>
            </div>
          </div>
          <span
            role="button"
            tabIndex={0}
            onClick={onClose}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClose(); }}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/15 text-white/70 hover:text-white cursor-pointer"
          >
            <X className="w-4.5 h-4.5" />
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Step 1: Choose content type */}
          <div>
            <label className="text-xs text-gray-500 mb-2 block" style={{ fontWeight: 600 }}>
              {"\u05DE\u05D4 \u05DC\u05D9\u05D9\u05E6\u05E8?"}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {GENERATION_TYPES.map((type) => (
                <span
                  key={type.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedType(type.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedType(type.id); } }}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 cursor-pointer select-none text-center ${
                    selectedType === type.id
                      ? "border-zap bg-zap-pale/50 shadow-sm"
                      : "border-gray-100 hover:border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <span className="text-lg">{type.icon}</span>
                  <span className={`text-xs ${selectedType === type.id ? "text-zap" : "text-gray-700"}`} style={{ fontWeight: 600 }}>
                    {type.label}
                  </span>
                </span>
              ))}
            </div>
          </div>

          {/* Step 2: Business context (optional) */}
          {selectedType && (
            <div>
              <label className="text-xs text-gray-500 mb-1.5 block" style={{ fontWeight: 600 }}>
                {"\u05E9\u05DD \u05E2\u05E1\u05E7 / \u05EA\u05D7\u05D5\u05DD (\u05D0\u05D5\u05E4\u05E6\u05D9\u05D5\u05E0\u05DC\u05D9)"}
              </label>
              <input
                type="text"
                value={businessContext}
                onChange={(e) => setBusinessContext(e.target.value)}
                placeholder={"\u05DC\u05DE\u05E9\u05DC: \u201C\u05E1\u05D8\u05D5\u05D3\u05D9\u05D5 \u05DC\u05E2\u05D9\u05E6\u05D5\u05D1 \u05D2\u05E8\u05E4\u05D9\u201D, \u201C\u05DE\u05E1\u05E2\u05D3\u05EA \u05E9\u05E3\u201D..."}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 outline-none focus:border-zap/50 focus:ring-1 focus:ring-zap/20 placeholder:text-gray-300"
                dir="rtl"
              />
            </div>
          )}

          {/* Step 3: Tone selection */}
          {selectedType && (
            <div>
              <label className="text-xs text-gray-500 mb-1.5 block" style={{ fontWeight: 600 }}>
                {"\u05D8\u05D5\u05DF"}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {TONE_PRESETS.map((preset) => (
                  <span
                    key={preset.label}
                    role="button"
                    tabIndex={0}
                    onClick={() => setTone(tone === preset.value ? "" : preset.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setTone(tone === preset.value ? "" : preset.value); } }}
                    className={`px-3 py-1.5 text-xs rounded-full cursor-pointer select-none ${
                      tone === preset.value
                        ? "bg-zap text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    style={{ fontWeight: 500 }}
                  >
                    {preset.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Custom instructions */}
          {selectedType && (
            <div>
              <label className="text-xs text-gray-500 mb-1.5 block" style={{ fontWeight: 600 }}>
                {"\u05D4\u05E0\u05D7\u05D9\u05D5\u05EA \u05E0\u05D5\u05E1\u05E4\u05D5\u05EA (\u05D0\u05D5\u05E4\u05E6\u05D9\u05D5\u05E0\u05DC\u05D9)"}
              </label>
              <textarea
                ref={textareaRef}
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder={"\u05DC\u05DE\u05E9\u05DC: \u201C\u05D4\u05D3\u05D2\u05E9 \u05D0\u05EA \u05D4\u05E0\u05D9\u05E1\u05D9\u05D5\u05DF \u05E9\u05DC 20 \u05E9\u05E0\u05D4\u201D, \u201C\u05D4\u05EA\u05DE\u05E7\u05D3 \u05D1\u05D0\u05D9\u05DB\u05D5\u05EA\u201D..."}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 resize-none outline-none focus:border-zap/50 focus:ring-1 focus:ring-zap/20 placeholder:text-gray-300"
                rows={2}
                dir="rtl"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleGenerate();
                  }
                }}
              />
            </div>
          )}

          {/* Generate button */}
          {selectedType && !generatedContent && !isGenerating && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleGenerate}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleGenerate(); } }}
              className="flex items-center justify-center gap-2 w-full py-3 bg-zap text-white rounded-xl hover:bg-zap-dark text-sm cursor-pointer select-none shadow-md"
              style={{ fontWeight: 600 }}
            >
              <Wand2 className="w-4 h-4" />
              {"\u05E6\u05D5\u05E8 \u05EA\u05D5\u05DB\u05DF"}
            </span>
          )}

          {/* Loading */}
          {isGenerating && (
            <div className="flex flex-col items-center justify-center gap-3 py-8">
              <div className="relative">
                <div className="w-12 h-12 rounded-full bg-zap-pale flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-zap animate-spin" />
                </div>
              </div>
              <span className="text-sm text-gray-500" style={{ fontWeight: 500 }}>
                {"\u05D9\u05D5\u05E6\u05E8 \u05EA\u05D5\u05DB\u05DF \u05DE\u05D5\u05EA\u05D0\u05DD \u05D0\u05D9\u05E9\u05D9\u05EA..."}
              </span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 leading-relaxed">
              {error}
              <span
                role="button"
                tabIndex={0}
                onClick={() => { setError(null); setGeneratedContent(null); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setError(null); setGeneratedContent(null); } }}
                className="block mt-2 text-xs text-red-400 hover:text-red-600 cursor-pointer underline"
              >
                {"\u05E0\u05E1\u05D4 \u05E9\u05D5\u05D1"}
              </span>
            </div>
          )}

          {/* Result */}
          {generatedContent && (
            <div className="space-y-3">
              <div className="bg-gradient-to-bl from-zap-pale/60 to-white border border-zap/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-zap" />
                    <span className="text-xs text-zap" style={{ fontWeight: 700 }}>
                      {"\u05EA\u05D5\u05E6\u05D0\u05D4"}
                    </span>
                  </div>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => handleCopy(generatedContent, -1)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleCopy(generatedContent, -1); } }}
                    className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-zap cursor-pointer px-2 py-1 rounded-md hover:bg-zap-pale/50"
                    style={{ fontWeight: 500 }}
                  >
                    {copiedIndex === -1 ? (
                      <>
                        <Check className="w-3 h-3 text-green-500" />
                        {"\u05D4\u05D5\u05E2\u05EA\u05E7!"}
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        {"\u05D4\u05E2\u05EA\u05E7"}
                      </>
                    )}
                  </span>
                </div>
                <p
                  className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap"
                  dir="rtl"
                >
                  {generatedContent}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => handleCopy(generatedContent, -1)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleCopy(generatedContent, -1); } }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 text-sm cursor-pointer select-none"
                  style={{ fontWeight: 600 }}
                >
                  <Copy className="w-3.5 h-3.5" />
                  {"\u05D4\u05E2\u05EA\u05E7 \u05D5\u05D4\u05D3\u05D1\u05E7 \u05D1\u05D0\u05EA\u05E8"}
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => { setGeneratedContent(null); handleGenerate(); }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setGeneratedContent(null); handleGenerate(); } }}
                  className="flex items-center justify-center gap-1.5 py-2.5 px-4 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 text-sm cursor-pointer select-none"
                  style={{ fontWeight: 600 }}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  {"\u05E0\u05E1\u05D4 \u05E9\u05D5\u05D1"}
                </span>
              </div>
            </div>
          )}

          {/* History */}
          {history.length > 1 && (
            <div>
              <label className="text-xs text-gray-400 mb-2 block" style={{ fontWeight: 600 }}>
                {"\u05D4\u05D9\u05E1\u05D8\u05D5\u05E8\u05D9\u05D4 (\u05D0\u05D7\u05E8\u05D5\u05E0\u05D9\u05DD)"}
              </label>
              <div className="space-y-2">
                {history.slice(1).map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start justify-between gap-2 p-2.5 bg-gray-50 rounded-lg border border-gray-100 text-xs text-gray-500"
                  >
                    <p className="flex-1 leading-relaxed whitespace-pre-wrap" dir="rtl">
                      {item.length > 100 ? item.substring(0, 100) + "..." : item}
                    </p>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => handleCopy(item, i)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleCopy(item, i); } }}
                      className="shrink-0 p-1 rounded hover:bg-gray-200 cursor-pointer text-gray-400 hover:text-gray-600"
                    >
                      {copiedIndex === i ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
